/* API utility functions for Magni Sign-in/out Application */

// Import constants and helpers
import { LOCATIONS, LOCATION_ICONS } from './constants.js';
import { formatTime, formatDate, formatDuration } from './date-helpers-consolidated.js';
import { EmployeeSignIns, SignInTableData, MagniEmployee, EventData } from '../components/Shared/Types.js';
import axios from 'axios';
import { IPublicClientApplication } from '@azure/msal-browser';
import { loginRequest } from './authConfig.js';
import { resolve } from 'path';
import moment from 'moment';


const fetchEmployeeData = async ():Promise<EmployeeSignIns[]> => {
  let users = await axios.get(`${process.env.REACT_APP_API_URL}/api/Calendar/GetAllEvents`);
      let u = await users.data;
      const parsedData = u.map((emp:EmployeeSignIns) => ({
        ...emp,
        signIns: emp.signIns.map((signIn) => ({
          ...signIn,
          eventDate: signIn.eventDate ? new Date(signIn.eventDate) : null,
        })),
      }));

      return parsedData;
};

const fetchEmployeeDataForSpecificDay = async (day:Date):Promise<EmployeeSignIns[]> => {
  let users = await axios.get(`${process.env.REACT_APP_API_URL}/api/Calendar/GetAllEvents?specificDate=` + new Date(moment.now()).toDateString());
      let u = await users.data;
      
      const parsedData = u.map((emp:EmployeeSignIns) => ({
        ...emp,
        signIns: emp.signIns.map((signIn) => ({
          ...signIn,
          eventDate: signIn.eventDate ? new Date(signIn.eventDate) : null,
        })),
      }));

      return parsedData;
};



const getMagniEmployees = async ():Promise<MagniEmployee[]> => {
  const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/Calendar/GetMagniEmployees`);
  const data = response.data;

  return data;
}


const recordEvent = async (eventData:SignInTableData):Promise<string> => {
  // Simulate API call delay

  const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/Calendar/CreateEvent`, eventData);

  return response.status === 200 ? 'Event recorded successfully' : 'Failed to record event';

  
};

const checkEventExistsForUser = async ( event:SignInTableData):Promise<SignInTableData> => {
  const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/Calendar/CheckForExistingEvent`, event);
  const data = await response.data;
  return data as SignInTableData;
}

const updateEvent = async (eventData:SignInTableData):Promise<string> => {
  const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/Calendar/UpdateEvent`, eventData);
  return response.status === 200 ? 'Event updated successfully' : 'Failed to update event';
};

const getEmployeesForManager = async (managerId: string):Promise<string[]> => {

  const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/Calendar/GetEmployeesForManager?managerName=${managerId}`);

  if (response.status !== 200) {
    return [];
  }
  const data = await response.data;

  

  return data;

}

const getSignInsForManager = async (employees:any[]):Promise<EmployeeSignIns[]> => {
  if (!employees || employees.length === 0) {
    return [];
  }
  const empNames = employees.map(e => e.username);
  const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/Calendar/GetEventsForEmployees`, empNames);

  const data = await response.data;

  if (response.status !== 200 || !data || data === 'No events found for the specified employees.') {
    return [];
  }
  return data;

}

const getSignInsForManagerOnDate = async (employees:any[], date:Date):Promise<EmployeeSignIns[]> => {
  if (!employees || employees.length === 0) {
    return [];
  }
  const empNames = employees.map(e => e.username);
  const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/Calendar/GetEventsForEmployees?day=`+date.toISOString(), empNames);

  const data = await response.data;

  const parsedData = data.map((emp:EmployeeSignIns) => ({
    ...emp,
    signIns: emp.signIns.map((signIn) => ({
      ...signIn,
      eventDate: signIn.eventDate ? new Date(signIn.eventDate) : null,
    })),
  }));

  if (response.status !== 200 || !data || data === 'No events found for the specified employees.') {
    return [];
  }

  return parsedData;

}

const getManagerInfo = async (msalInstance: IPublicClientApplication):Promise<any> => {
  const account = msalInstance.getAllAccounts()[0];
  const tokenRequest = {
    scopes: ["User.Read"],
    account: account // This scope is for Microsoft Graph
  };

  const token = await msalInstance.acquireTokenSilent(tokenRequest);
  //use fetch to avoid axios interceptors
  const response = await fetch("https://graph.microsoft.com/v1.0/me/manager", {
    headers: {
      Authorization: `Bearer ${token.accessToken}`
    }
  });

  return response.json();
}

const getSignInsForLocation = async (location: string, magniEmployees: boolean):Promise<EmployeeSignIns[]> => {
  const url = magniEmployees ?
    `${process.env.REACT_APP_API_URL}/api/Calendar/GetEventsForLocation?location=${location}&isMagniEmployee=true` :
    `${process.env.REACT_APP_API_URL}/api/Calendar/GetEventsForLocation?location=${location}`;
  const response = await axios.get(url);
  const data = await response.data;
  if (response.status !== 200 || !data) {
    return [];
  }
  const parsedData = data.map((emp:EmployeeSignIns) => ({
    ...emp,
    signIns: emp.signIns.map((signIn) => ({
      ...signIn,
      eventDate: signIn.eventDate ? new Date(signIn.eventDate) : null,
    })),
  }));
  return parsedData;
}


// Export all functions
export {
  fetchEmployeeData,
  recordEvent,
  getMagniEmployees,
  getSignInsForManager,
  getEmployeesForManager,
  getManagerInfo,
  checkEventExistsForUser,
  updateEvent,
  getSignInsForLocation,
  getSignInsForManagerOnDate,
  fetchEmployeeDataForSpecificDay
};